import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import type { Health } from '../api/types.ts';
import { connectionState } from './connectionState.ts';

export type { ConnectionState } from './connectionState.ts';

function subscribe(listener: () => void) {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}

/** A check that gets no answer in this time is a failed check, not a check forever "running". */
const CHECK_TIME_LIMIT_MS = 10_000;

/**
 * The admin web is online-only; the indicator still says plainly whether the browser has internet
 * and whether the server answers (CLAUDE.md §3.3.5). Health is public and checked every 30 s.
 * Until the first check has answered, the state is "checking" — never "online" (connectionState.ts).
 */
export function useConnection() {
  const browserOnline = useSyncExternalStore(subscribe, () => navigator.onLine);
  const health = useQuery({
    queryKey: ['health'],
    queryFn: async (): Promise<Health | null> => {
      const response = await fetch('/api/health', {
        cache: 'no-store',
        signal: AbortSignal.timeout(CHECK_TIME_LIMIT_MS),
      });
      // 503 still carries the body (which dependency is down).
      if (response.status !== 200 && response.status !== 503) return null;
      return (await response.json()) as Health;
    },
    refetchInterval: 30_000,
    retry: false,
    // A screen that mounts another indicator must not restart the check (M7-5: the sign-in screen
    // switching to offline sign-in remounted it, which read "online" again and switched back).
    retryOnMount: false,
  });
  const state = connectionState({
    browserOnline,
    answeredAt: health.dataUpdatedAt,
    failedAt: health.errorUpdatedAt,
    answerUnusable: health.data === null,
  });
  return {
    state,
    health: health.data ?? null,
    checkedAt: health.dataUpdatedAt || null,
    checking: health.isFetching,
    recheck: () => void health.refetch(),
  };
}
